import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Loader2,
    FileText,
    ArrowRight,
    Store,
    Download,
    HistoryIcon,
    Wallet,
    Package
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    fetchProductSalesReport,
    fetchProductSalesReporteExportCSV,
    fetchSalesPerformanceExportCSV,
    fetchStoresWhereStaffAssigned
} from '../apiHelper';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';

const formatDate = (date) => date.toISOString().split('T')[0];

const SalesReport = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [loading, setLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [reportLines, setReportLines] = useState([]);

    const [range, setRange] = useState({
        start_date: location.state?.passedStartDate || formatDate(new Date(new Date().setDate(new Date().getDate() - 30))),
        end_date: location.state?.passedEndDate || formatDate(new Date())
    });

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) navigate('/login');
        else setUser(JSON.parse(lcUser));
    }, [navigate]);

    useEffect(() => {
        if (user) {
            fetchStoresWhereStaffAssigned(user).then(allStores => {
                const rtlStores = allStores.filter(s => s.storetype === 'RTL');
                setStores(rtlStores);
                if (rtlStores.length > 0) setSelectedStore(rtlStores[0]);
            });
        }
    }, [user]);

    const loadData = async () => {
        if (!selectedStore) return;
        setLoading(true);
        try {
            const results = await fetchProductSalesReport({ ...range, store_id: selectedStore.store_id });
            console.log(results)
            setReportLines(results?.lines || []);
        } catch (err) {
            showToast("Failed to load product sales report", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [range, selectedStore]);

    // Calculate aggregated totals from line items
    const summary = useMemo(() => {
        return reportLines.reduce((acc, line) => {
            acc.totalQty += Number(line.quantity_sold || 0);
            acc.totalSales += Number(line.total_sales_value || 0);
            acc.totalDiscount += Number(line.discount_value || 0);
            acc.totalCogs += Number(line.cogs || 0);
            return acc;
        }, { totalQty: 0, totalSales: 0, totalDiscount: 0, totalCogs: 0 });
    }, [reportLines]);

    const exportToCSV = async () => {
        setCsvLoading(true);
        try {
            // const baseCsvContent = await fetchSalesPerformanceExportCSV({ ...range, store_id: selectedStore.store_id });
            const baseCsvContent = await fetchProductSalesReporteExportCSV({ ...range, store_id: selectedStore.store_id });

            let extraContent = `\n\nPRODUCT SALES SUMMARY\n`;
            extraContent += `Product Name,Part Number,Quantity Sold,Total Sales Value,Discount Value,Unit Cost,COGS\n`;

            reportLines.forEach(line => {
                extraContent += `"${line.productname}","${line.partnumber}",${line.quantity_sold},${line.total_sales_value},${line.discount_value},${line.unit_cost},${line.cogs}\n`;
            });

            extraContent += `\nTOTALS,,${summary.totalQty},${summary.totalSales.toFixed(2)},${summary.totalDiscount.toFixed(2)},,${summary.totalCogs.toFixed(2)}\n`;

            const blob = new Blob([baseCsvContent + extraContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Product_Sales_Report_${selectedStore?.identifier || 'Store'}_${range.start_date}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            showToast("CSV exported successfully", "success");
        } catch (err) {
            showToast("CSV Export failed", 'error');
        } finally {
            setCsvLoading(false);
        }
    };

    // const generatePDF = () => {
    //     const doc = new jsPDF('l', 'mm', 'a4');
    //     doc.setFontSize(18);
    //     doc.text("Product Sales Report", 14, 15);

    //     autoTable(doc, {
    //         startY: 25,
    //         head: [['Metric Summary', 'Value']],
    //         body: [
    //             ['Total Items Sold', summary.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 })],
    //             ['Total Sales Value (NGN)', Number(summary.totalSales).toLocaleString(undefined, { minimumFractionDigits: 2 })],
    //             ['Total Discount Value (NGN)', Number(summary.totalDiscount).toLocaleString(undefined, { minimumFractionDigits: 2 })],
    //             ['Total COGS (NGN)', Number(summary.totalCogs).toLocaleString(undefined, { minimumFractionDigits: 2 })]
    //         ],
    //         theme: 'grid',
    //         headStyles: { fillColor: [79, 70, 229] },
    //         styles: { fontSize: 9, fontStyle: 'bold' },
    //         margin: { right: 180 }
    //     });

    //     const tableRows = reportLines.map(line => [
    //         line.productname,
    //         line.partnumber,
    //         Number(line.quantity_sold).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    //         Number(line.total_sales_value).toLocaleString(undefined, { minimumFractionDigits: 2 }),
    //         Number(line.discount_value).toLocaleString(undefined, { minimumFractionDigits: 2 }),
    //         Number(line.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 2 }),
    //         Number(line.cogs).toLocaleString(undefined, { minimumFractionDigits: 2 })
    //     ]);

    //     autoTable(doc, {
    //         startY: doc.lastAutoTable.finalY + 10,
    //         head: [['Product Name', 'Part Number', 'Qty Sold', 'Total Sales', 'Discount', 'Unit Cost', 'COGS']],
    //         body: tableRows,
    //         theme: 'striped',
    //         headStyles: { fillColor: [31, 41, 55] },
    //         styles: { fontSize: 8 }
    //     });

    //     doc.save(`Product_Sales_Report_${selectedStore?.identifier || 'Store'}.pdf`);
    // };

    const generatePDF = () => {
        // 1. Initialize Landscape PDF
        const doc = new jsPDF('l', 'mm', 'a4');

        // 2. Add Title & Store Information Header
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text("Product Sales Report", 14, 15);

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`Store: ${selectedStore?.identifier || 'N/A'} | Period: ${range.start_date} to ${range.end_date}`, 14, 21);

        // 3. Render Metric Summary Box
        autoTable(doc, {
            startY: 25,
            head: [['Metric Summary', 'Value']],
            body: [
                ['Total Items Sold', summary.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 })],
                ['Total Sales Value (NGN)', Number(summary.totalSales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
                ['Total Discount Value (NGN)', Number(summary.totalDiscount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
                ['Total COGS (NGN)', Number(summary.totalCogs).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })]
            ],
            theme: 'grid',
            headStyles: { fillColor: [79, 70, 229] },
            styles: { fontSize: 8.5 },
            margin: { right: 180 }
        });

        // 4. Map API Response Lines to Table Rows
        const tableRows = reportLines.map(line => [
            line.productname,
            line.partnumber,
            Number(line.quantity_sold || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
            Number(line.total_sales_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            Number(line.discount_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            Number(line.unit_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            Number(line.cogs || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        ]);

        // 5. Render Detailed Product Lines Table
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 8,
            head: [['Product Name', 'Part Number', 'Qty Sold', 'Total Sales (NGN)', 'Discount (NGN)', 'Unit Cost (NGN)', 'COGS (NGN)']],
            body: tableRows,
            foot: [[
                'TOTALS',
                '',
                summary.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                summary.totalSales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                summary.totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                '-',
                summary.totalCogs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ]],
            theme: 'striped',
            headStyles: { fillColor: [31, 41, 55], fontStyle: 'bold' },
            footStyles: { fillColor: [243, 244, 246], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 8 },
            columnStyles: {
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'right' },
                5: { halign: 'right' },
                6: { halign: 'right' }
            }
        });

        // 6. Save PDF
        doc.save(`Product_Sales_Report_${selectedStore?.identifier || 'Store'}_${range.start_date}.pdf`);
    };

    return (
        <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
                        <HistoryIcon className="text-indigo-600" size={24} /> Product Sales Report
                    </h1>
                    <p className="text-gray-500 text-xs">Product performance breakdown by store and date range</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex flex-col sm:flex-row bg-white p-2 rounded-lg border shadow-sm items-center gap-2 sm:gap-4">
                        <div className="flex items-center gap-2 px-2 sm:border-r sm:pr-4 w-full sm:w-auto">
                            <Store size={16} className="text-gray-400" />
                            <select
                                className="text-xs font-bold border-none bg-transparent focus:ring-0 cursor-pointer w-full"
                                value={selectedStore?.store_id || ''}
                                onChange={(e) => setSelectedStore(stores.find(s => s.store_id == e.target.value))}
                            >
                                {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.identifier}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-[11px] font-bold w-full sm:w-auto">
                            <input type="date" value={range.start_date} onChange={e => setRange({ ...range, start_date: e.target.value })} className="border-none p-1 bg-gray-50 rounded w-full sm:w-auto" />
                            <ArrowRight size={14} className="text-gray-300" />
                            <input type="date" value={range.end_date} onChange={e => setRange({ ...range, end_date: e.target.value })} className="border-none p-1 bg-gray-50 rounded w-full sm:w-auto" />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button onClick={exportToCSV} disabled={loading || csvLoading} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border px-4 py-2 rounded-lg text-xs font-bold shadow-sm hover:bg-gray-50">
                            {csvLoading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} CSV
                        </button>
                        <button onClick={generatePDF} disabled={loading} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-md hover:bg-indigo-700">
                            <FileText size={14} /> PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile View: Cards */}
            <div className="lg:hidden space-y-4">
                {loading ? (
                    <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" /></div>
                ) : reportLines.length === 0 ? (
                    <div className="py-20 text-center text-gray-400 italic bg-white rounded-lg border">No records found</div>
                ) : (
                    reportLines.map((line) => (
                        <div key={line.catentry_id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-900 text-sm">{line.productname}</h3>
                                    <span className="text-[10px] text-gray-400 font-mono block">{line.partnumber}</span>
                                </div>
                                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
                                    <Package size={12} /> {Number(line.quantity_sold).toLocaleString(undefined, { maximumFractionDigits: 2 })} sold
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-[11px] pt-3 border-t mt-3">
                                <div>
                                    <span className="text-gray-400 block text-[10px]">Sales Value</span>
                                    <span className="font-bold text-emerald-600">₦{Number(line.total_sales_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div>
                                    <span className="text-gray-400 block text-[10px]">COGS</span>
                                    <span className="font-bold text-gray-700">₦{Number(line.cogs).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div>
                                    <span className="text-gray-400 block text-[10px]">Unit Cost</span>
                                    <span className="text-gray-600">₦{Number(line.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div>
                                    <span className="text-gray-400 block text-[10px]">Discount</span>
                                    <span className="text-rose-500">₦{Number(line.discount_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        </div>
                    ))
                )}

                {/* Mobile Totals Card */}
                {!loading && reportLines.length > 0 && (
                    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-lg mt-6">
                        <div className="flex items-center gap-2 mb-3 border-b border-gray-700 pb-2">
                            <Wallet size={16} className="text-indigo-400" />
                            <span className="text-xs font-bold uppercase tracking-wider">Period Summary</span>
                        </div>
                        <div className="grid grid-cols-2 gap-y-3">
                            <div className="text-[10px] text-gray-400 uppercase">Total Qty Sold</div>
                            <div className="text-xs font-bold text-right text-indigo-300">{summary.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                            <div className="text-[10px] text-gray-400 uppercase">Total Sales Value</div>
                            <div className="text-xs font-bold text-right text-emerald-400">₦{summary.totalSales.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                            <div className="text-[10px] text-gray-400 uppercase">Total COGS</div>
                            <div className="text-xs font-bold text-right text-gray-300">₦{summary.totalCogs.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                            <div className="text-[10px] text-gray-100 uppercase font-black border-t border-gray-700 pt-2">Total Discounts</div>
                            <div className="text-xs font-black text-right text-rose-400 border-t border-gray-700 pt-2">₦{summary.totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop View: Table */}
            <Card className="hidden lg:block overflow-hidden border-none shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800 text-white">
                            <tr>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Product Name</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase">Part Number</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Qty Sold</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Total Sales</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Discount</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">Unit Cost</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase text-right">COGS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan="7" className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" /></td></tr>
                            ) : reportLines.length === 0 ? (
                                <tr><td colSpan="7" className="py-20 text-center text-gray-400 italic">No records found for this period</td></tr>
                            ) : (
                                reportLines.map((line) => (
                                    <tr key={line.catentry_id} className="hover:bg-gray-50 text-xs text-gray-700 font-medium">
                                        <td className="px-4 py-3 font-bold text-gray-900">{line.productname}</td>
                                        <td className="px-4 py-3 text-gray-500 font-mono text-[11px]">{line.partnumber}</td>
                                        <td className="px-4 py-3 text-right font-bold text-indigo-600">
                                            {Number(line.quantity_sold).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-600">
                                            ₦{Number(line.total_sales_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-3 text-right text-rose-400">
                                            ₦{Number(line.discount_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-500">
                                            ₦{Number(line.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                            ₦{Number(line.cogs).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {!loading && reportLines.length > 0 && (
                            <tfoot className="bg-gray-50 font-black border-t-2 border-gray-200">
                                <tr className="text-xs text-gray-900">
                                    <td colSpan="2" className="px-4 py-4 text-right uppercase tracking-wider">Totals</td>
                                    <td className="px-4 py-4 text-right text-indigo-600">
                                        {summary.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-4 text-right text-emerald-600">
                                        ₦{summary.totalSales.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-4 text-right text-rose-500">
                                        ₦{summary.totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-4 text-right text-gray-400">-</td>
                                    <td className="px-4 py-4 text-right">
                                        ₦{summary.totalCogs.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default SalesReport;